import clsx from 'clsx'

import { CLI_INSTALL_URL, PAGE_ROUTES, TERMIBLE_KEY } from '~/constants'
import TerminalSessionProvider from '~/lib/termible/TerminalSessionProvider'
import Text from '~/components/Text/Text'
import Link from '~/components/Link/Link'
import Button from '~/components/Button/Button'
import CodeBlock from '~/components/CodeBlock/CodeBlock'
import CodeInline from '~/components/CodeInline/CodeInline'
import { FCWithChildren } from '~/lib/types'

import styles from './CLIGettingStarted.module.sass'

const Label: FCWithChildren = ({ children }) => (
  <Text fontSize={[18, 14, 18]} className={styles.label}>
    {children}
  </Text>
)

const Command: FCWithChildren = ({ children }) => (
  <CodeBlock language='bash' className={styles.command} interactive>
    {children}
  </CodeBlock>
)

const CLIGettingStarted: React.FC = () => (
  <TerminalSessionProvider termibleKey={TERMIBLE_KEY}>
    <div className={clsx('site-grid site-container', styles.section)} data-testid='cli-getting-started-section'>
      <div className='col-span-2 md:col-span-6 md:col-start-2 lg:col-start-4'>
        <Text variant='subheading' fontSize={[28, 28, 40]} className={styles.heading}>
          Getting started
        </Text>

        <div className={styles.commandWrapper}>
          <Label>
            HTTPie{' '}
            <Link href={CLI_INSTALL_URL} underlined>
              installs
            </Link>{' '}
            <CodeInline>http</CodeInline> and <CodeInline>https</CodeInline>:
          </Label>
        </div>

        <div className={styles.commandWrapper}>
          <Label>Hello World:</Label>
          <Command>$ https httpie.io/hello</Command>
        </div>

        <div className={styles.commandWrapper}>
          <Label>
            Custom{' '}
            <Link href={`${PAGE_ROUTES.DOCS_CLI}/http-method`} underlined external>
              HTTP method
            </Link>
            ,{' '}
            <Link href={`${PAGE_ROUTES.DOCS_CLI}/http-headers`} underlined external>
              HTTP headers
            </Link>{' '}
            and{' '}
            <Link href={`${PAGE_ROUTES.DOCS_CLI}/json`} underlined external>
              JSON
            </Link>{' '}
            data:
          </Label>
          <Command>$ http PUT pie.dev/put X-API-Token:123 name=John</Command>
        </div>

        <div className={styles.commandWrapper}>
          <Label>
            Submitting{' '}
            <Link href={`${PAGE_ROUTES.DOCS_CLI}/forms`} underlined external>
              forms
            </Link>
            :
          </Label>
          <Command>$ http -f POST pie.dev/post hello=World</Command>
        </div>

        <div className={styles.commandWrapper}>
          <Label>
            See the request that is being sent using one of the{' '}
            <Link href={`${PAGE_ROUTES.DOCS_CLI}/output-options`} underlined external>
              output options
            </Link>
            :
          </Label>
          <Command>$ http -v pie.dev/get</Command>
        </div>

        <div className={styles.commandWrapper}>
          <Label>
            Build and print a request without sending it using{' '}
            <Link href={`${PAGE_ROUTES.DOCS_CLI}/offline-mode`} underlined external>
              offline mode
            </Link>
            :
          </Label>
          <Command>$ http --offline pie.dev/post hello=offline</Command>
        </div>

        <div className={styles.commandWrapper}>
          <Label>
            Use{' '}
            <Link href='https://developer.github.com/v3/issues/comments/#create-a-comment' underlined>
              Github API
            </Link>{' '}
            to post a comment on an{' '}
            <Link href='https://github.com/httpie/cli/issues/83' underlined>
              issue
            </Link>{' '}
            with{' '}
            <Link href={`${PAGE_ROUTES.DOCS_CLI}/authentication`} underlined external>
              authentication
            </Link>
            :
          </Label>
          <Command>
            $ http -a USERNAME POST https://api.github.com/repos/httpie/cli/issues/83/comments body=HTTPie is
            awesome! :heart:
          </Command>
        </div>

        <div className={styles.commandWrapper}>
          <Label>
            Upload a file using{' '}
            <Link href={`${PAGE_ROUTES.DOCS_CLI}/redirected-input`} underlined external>
              redirected input
            </Link>
            :
          </Label>
          <Command>{'$ http pie.dev/post < files/data.json'}</Command>
        </div>

        <div className={styles.commandWrapper}>
          <Label>
            Download a file and save it via{' '}
            <Link href={`${PAGE_ROUTES.DOCS_CLI}/redirected-output`} underlined external>
              redirected output
            </Link>
            :
          </Label>
          <Command>{'$ http pie.dev/image/png > image.png'}</Command>
        </div>

        <div className={styles.commandWrapper}>
          <Label>
            Download a file <CodeInline>wget</CodeInline> style:
          </Label>
          <Command>$ http --download pie.dev/image/png</Command>
        </div>

        <div className={styles.commandWrapper}>
          <Label>
            Use named{' '}
            <Link href={`${PAGE_ROUTES.DOCS_CLI}/sessions`} underlined external>
              sessions
            </Link>{' '}
            to make certain aspects of the communication persistent between requests to the same host:
          </Label>
          <Command>$ http --session=logged-in -a username:password pie.dev/get API-Key:123</Command>
          <Command>$ http --session=logged-in pie.dev/headers</Command>
        </div>

        <div className={styles.commandWrapper}>
          <Label>
            Set a custom <CodeInline>Host</CodeInline> header to work around missing DNS records:
          </Label>
          <Command>$ http localhost:8000 Host:example.com</Command>
        </div>
      </div>

      <div className='flex row-start-2 col-span-full justify-center'>
        <Button
          variant='primary'
          className={styles.button}
          link={PAGE_ROUTES.DOCS_CLI}
          data-track='cta:learn-more-about-cli:from-cli-page-examples'
        >
          Learn more in the docs
        </Button>
      </div>
    </div>
  </TerminalSessionProvider>
)

export default CLIGettingStarted
